import React, { useEffect, useState } from "react";
import { useOutlet } from "react-router-dom";
import {
  Box,
  useMediaQuery,
} from "@mui/material";
import { auth } from "../../firebase/firebase";
import SidebarLinguist from "components/SidebarLinguist";
import NavbarLinguist from "components/NavbarLinguist";
import LinguistOnboardingTour from "components/LinguistOnboardingTour";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import {
  LINGUIST_ONBOARDING_RESTART_EVENT,
  hasSeenLinguistOnboarding,
  markLinguistOnboardingSeen,
} from "helpers/linguistOnboarding";
import { NAVBAR_HEIGHT } from "constants/layout";

const LinguistPortalLayout = () => {
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const { currentUser } = useAuth();
  const outlet = useOutlet();

  // First-visit onboarding tour (design spec: ysera-linguist-onboarding-spec.md)
  const [tourOpen, setTourOpen] = useState(false);

  // Use the context for sidebar state management
  const {
    activeMenu,
    setActiveMenu,
    screenSize,
    setScreenSize,
    isCollapsed,
  } = useStateContext();

  // Define drawer widths
  const drawerWidth = 288;
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25); // 25% of full width

  // Calculate current drawer width based on collapsed state
  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  // `auth.currentUser` is not reactive, so a memo keyed on `currentUser.email`
  // can stay stuck on the fallback when the Firebase user hydrates after the
  // AuthContext user. Derive at render instead (cheap computation).
  const userEmail = String(
    currentUser?.email || auth.currentUser?.email || "Linguist User",
  );

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setScreenSize]);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize, setActiveMenu]);

  // Open the tour once on first entry (key absent). 400ms lets the shell paint
  // first and avoids a flash-of-dialog on cold load (spec §1).
  useEffect(() => {
    if (hasSeenLinguistOnboarding()) return undefined;
    const timer = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(timer);
  }, []);

  // Replay from Profile → Notifications ("Take the tour again", spec §5).
  useEffect(() => {
    const handleRestart = () => setTourOpen(true);
    window.addEventListener(LINGUIST_ONBOARDING_RESTART_EVENT, handleRestart);
    return () =>
      window.removeEventListener(LINGUIST_ONBOARDING_RESTART_EVENT, handleRestart);
  }, []);

  // Single writer for the seen key — every dismissal path funnels here.
  const handleTourClose = () => {
    markLinguistOnboardingSeen();
    setTourOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <SidebarLinguist
        user={currentUser}
        drawerWidth={drawerWidth}
        isNonMobile={isNonMobile}
      />

      {/* Main Content Container */}
      <Box
        sx={{
          flexGrow: 1,
          width:
            isNonMobile && activeMenu
              ? `calc(100% - ${currentDrawerWidth}px)`
              : "100%",

          ml: isNonMobile && activeMenu ? `${currentDrawerWidth}px` : 0,
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          transition: (theme) =>
            theme.transitions.create(["width", "margin-left"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          bgcolor: "background.default",
        }}
      >
        {/* Navbar - Fixed at top */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            minHeight: NAVBAR_HEIGHT,
            bgcolor: "background.default",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <NavbarLinguist user={{ ...currentUser, Email: userEmail }} />
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
            }}
          >
            <Box>{outlet}</Box>
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              height: "20px",
              minHeight: "20px",
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: "background.default",
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </Box>
      </Box>

      <LinguistOnboardingTour open={tourOpen} onClose={handleTourClose} />
    </Box>
  );
};

export default LinguistPortalLayout;
